import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import './App.css';
import config from './config';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './hooks/useAuth';
import { ThemeProvider } from './contexts/ThemeContext';
import { CourseTimeProvider } from './contexts/CourseTimeContext';
import { Toaster } from './components/ui/toaster';
import './lib/i18n'; // Initialize i18n

// Auth Components
import Login from './components/auth/Login';
import VerifyEmail from './components/auth/VerifyEmail';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Layout Components
import AppLayout from './components/layout/AppLayout';

// Page Components
import Dashboard from './components/pages/Dashboard';
import FamilyDashboard from './components/pages/FamilyDashboard';
import MyStudentDetail from './components/pages/MyStudentDetail';
import Profile from './components/pages/Profile';
import Settings from './components/pages/Settings';
import Communications from './components/pages/Communications';
import EmailCreationWizard from './components/communications/EmailCreationWizard';
import StudentsPage from './components/students/StudentsPage';
import StudentDetail from './components/students/StudentDetail';
import HouseSettings from './components/pages/HouseSettings';
import HouseAllocation from './components/pages/HouseAllocation';
import ClassAllocation from './components/pages/ClassAllocation';
import CuntoiriManagement from './components/pages/CuntoiriManagement';
import PaymentsPage from './components/pages/PaymentsPage';
import AuditLogPage from './components/pages/AuditLogPage';
import IdentityReviewQueue from './components/pages/IdentityReviewQueue';
import NotFound from './components/pages/NotFound';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import ErrorBoundary from './components/ErrorBoundary';
import ApiTest from './components/ApiTest';

// Coming Soon placeholder component
function ComingSoonPage({ featureName }) {
  const { t } = useTranslation();
  return (
    <div className="text-center py-8">
      {featureName} - {t('comingSoon')}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <CourseTimeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              {/* Dashboard Route */}
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute pageKey="dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              
              {/* Family Dashboard Route — PARENT only */}
              <Route
                path="family-dashboard"
                element={
                  <ProtectedRoute pageKey="family-dashboard" strictUserTypes={['PARENT']}>
                    <FamilyDashboard />
                  </ProtectedRoute>
                }
              />

              {/* My Student Detail — PARENT only */}
              <Route
                path="my-student/:uuid"
                element={
                  <ProtectedRoute strictUserTypes={['PARENT']}>
                    <MyStudentDetail />
                  </ProtectedRoute>
                }
              />

              {/* Profile Route - All authenticated users */}
              <Route path="profile" element={<Profile />} />
              
              {/* Student Management Routes */}
              <Route
                path="students"
                element={
                  <ProtectedRoute pageKey="students">
                    <StudentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students/:uuid"
                element={
                  <ProtectedRoute pageKey="students">
                    <StudentDetail />
                  </ProtectedRoute>
                }
              />
              
              {/* Development/Testing Routes */}
              {config.features.debugMode && (
                <Route path="api-test" element={<ApiTest />} />
              )}
              
              {/* Placeholder routes - will be implemented in next phase */}
              {/* Settings Route */}
              <Route
                path="settings"
                element={
                  <ProtectedRoute pageKey="settings">
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route path="audit-log" element={<ProtectedRoute pageKey="audit-log"><AuditLogPage /></ProtectedRoute>} />

              <Route path="courses" element={<ComingSoonPage featureName="Cúrsaí / Courses" />} />
              <Route
                path="houses"
                element={
                  <ProtectedRoute pageKey="houses">
                    <HouseSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="house-allocation"
                element={
                  <ProtectedRoute pageKey="house-allocation">
                    <HouseAllocation />
                  </ProtectedRoute>
                }
              />
              <Route path="cuntoirs" element={<CuntoiriManagement />} />
              <Route path="classes" element={<ComingSoonPage featureName="Ranganna / Classes" />} />
              <Route
                path="class-allocation"
                element={
                  <ProtectedRoute pageKey="class-allocation">
                    <ClassAllocation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="payments"
                element={
                  <ProtectedRoute strictUserTypes={['ADMIN', 'OFFICE']}>
                    <PaymentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="students/identity-reviews"
                element={
                  <ProtectedRoute strictUserTypes={['ADMIN', 'OFFICE']}>
                    <IdentityReviewQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications"
                element={
                  <ProtectedRoute pageKey="communications">
                    <Communications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications/create"
                element={
                  <ProtectedRoute pageKey="communications">
                    <EmailCreationWizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="communications/edit/:draftUuid"
                element={
                  <ProtectedRoute pageKey="communications">
                    <EmailCreationWizard />
                  </ProtectedRoute>
                }
              />
              <Route path="travel" element={<ComingSoonPage featureName="Taisteal / Travel" />} />
            </Route>

            {/* 404 Not Found Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>

          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
    </CourseTimeProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
